import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, btnStyle, inputStyle, labelStyle } from '../theme';

const VENDORS = ['itero', '3shape', 'medit', 'other', 'manual'];

const ScannerImportPage = ({ API_BASE, patientsList }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ patientId: '', vendor: 'itero', caseId: '', notes: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const { data } = await axios.get(`${API_BASE}/scans`); setList(data); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upload = async () => {
    if (!file) return alert('Select a scan file (STL/OBJ/PLY/ZIP)');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('scan', file);
      Object.keys(form).forEach(k => form[k] && fd.append(k, form[k]));
      await axios.post(`${API_BASE}/scans/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null); setForm({ patientId: '', vendor: 'itero', caseId: '', notes: '' });
      load();
      alert('Scan uploaded');
    } catch (e) { alert('Upload failed: ' + (e.response?.data?.error || e.message)); }
    finally { setUploading(false); }
  };

  const trySync = async (vendor) => {
    try {
      const { data } = await axios.post(`${API_BASE}/scans/sync/${vendor}`);
      alert(data.message);
    } catch (e) {
      const msg = e.response?.data;
      alert(`${msg?.error || 'Sync unavailable'}\n\n${msg?.hint || ''}`);
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this scan file?')) return;
    try { await axios.delete(`${API_BASE}/scans/${id}`); load(); } catch {}
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <h2 style={{ margin: 0, color: theme.navy }}>🦷 Intraoral Scanner Import</h2>
        <p style={{ margin: '6px 0', fontSize: 13, color: theme.textMuted }}>
          Upload STL/OBJ/PLY/ZIP files exported from iTero, 3Shape TRIOS, or Medit i500. Auto-sync activates once vendor API keys are configured in environment.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <button onClick={() => trySync('itero')} style={{ ...btnStyle, marginTop: 0, background: '#0891b2' }}>Try iTero Sync</button>
          <button onClick={() => trySync('3shape')} style={{ ...btnStyle, marginTop: 0, background: '#7c3aed' }}>Try 3Shape Sync</button>
          <button onClick={() => trySync('medit')} style={{ ...btnStyle, marginTop: 0, background: '#059669' }}>Try Medit Sync</button>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <h3 style={{ margin: '0 0 10px' }}>Upload New Scan</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>Patient</label>
            <select value={form.patientId} onChange={e => setForm({ ...form, patientId: e.target.value })} style={inputStyle}>
              <option value="">Select…</option>
              {(patientsList || []).map(p => <option key={p._id} value={p._id}>{p.name} ({p.uhid})</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>Vendor</label>
            <select value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} style={inputStyle}>
              {VENDORS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>Case ID</label>
            <input value={form.caseId} onChange={e => setForm({ ...form, caseId: e.target.value })} style={inputStyle} placeholder="Vendor case #"/>
          </div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Notes</label>
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={inputStyle}/>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <input type="file" accept=".stl,.obj,.ply,.zip,.3oxz,.dcm" onChange={e => setFile(e.target.files[0])}/>
          </div>
        </div>
        <button onClick={upload} disabled={uploading} style={{ ...btnStyle, marginTop: 10 }}>
          {uploading ? 'Uploading…' : 'Upload Scan'}
        </button>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 10px' }}>Imported Scans ({list.length})</h3>
        {list.length === 0 ? <p style={{ color: theme.textMuted }}>No scans yet.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: theme.bg, textAlign: 'left' }}>
                <th style={{ padding: 10 }}>Patient</th>
                <th style={{ padding: 10 }}>Vendor</th>
                <th style={{ padding: 10 }}>File</th>
                <th style={{ padding: 10 }}>Size</th>
                <th style={{ padding: 10 }}>Case</th>
                <th style={{ padding: 10 }}>Uploaded</th>
                <th style={{ padding: 10 }}></th>
              </tr>
            </thead>
            <tbody>
              {list.map(s => (
                <tr key={s._id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: 10 }}>{s.patientId?.name || '—'}</td>
                  <td style={{ padding: 10, textTransform: 'uppercase' }}>{s.vendor}</td>
                  <td style={{ padding: 10, fontSize: 12, fontFamily: 'monospace' }}>{s.fileName}</td>
                  <td style={{ padding: 10 }}>{(s.sizeBytes / 1024 / 1024).toFixed(1)} MB</td>
                  <td style={{ padding: 10 }}>{s.caseId || '—'}</td>
                  <td style={{ padding: 10 }}>{new Date(s.createdAt).toLocaleDateString()}</td>
                  <td style={{ padding: 10 }}>
                    <a href={`${API_BASE}/scans/${s._id}/download`} style={{ marginRight: 10, color: theme.primary }}>Download</a>
                    <button onClick={() => del(s._id)} style={{ color: theme.danger, background: 'none', border: 'none', cursor: 'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ScannerImportPage;
